import React, { useState, type PropsWithChildren, type ReactNode } from "react";
import cx from "classnames";
import { motion, type Transition } from "motion/react";
import { ChevronDownIcon as ChevronDown } from "@picklebet/ui";
import { Collapse } from "@picklebet/ui";

import * as styles from "@/features/betslip/components/parts/BetslipSection.module.scss";

type BetslipSectionProps = PropsWithChildren<{
  label: string;
  badge?: ReactNode;
  info?: ReactNode;
  count?: number;
  testId?: string;
  defaultOpen?: boolean;
  /** First section hangs off the tab bar as a full-bleed banner; the rest
   *  are standalone rounded pills. */
  attached?: boolean;
}>;

const CARET_TRANSITION: Transition = { duration: 0.2, ease: "easeOut" };

const COLLAPSE_TRANSITION: Transition = { duration: 0.22, ease: "easeOut" };

export const BetslipSection = ({
  label,
  badge,
  info,
  count,
  testId,
  defaultOpen = true,
  attached = false,
  children,
}: BetslipSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={styles.section} data-testid={testId}>
      <button
        type="button"
        className={cx(
          styles.header,
          attached ? styles.attached : styles.standalone,
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={styles.label}>
          {label}
          {info}
        </span>
        {badge ? <span className={styles.badge}>{badge}</span> : null}
        {typeof count === "number" && count > 0 ? (
          <span className={styles.count} data-testid={`${testId}-count`}>
            {count}
          </span>
        ) : null}
        <motion.span
          className={styles.caret}
          animate={{ rotate: open ? 180 : 0 }}
          transition={CARET_TRANSITION}
        >
          <ChevronDown />
        </motion.span>
      </button>
      <Collapse
        isOpen={open}
        skipInitial
        className={styles.content}
        transition={COLLAPSE_TRANSITION}
      >
        {children}
      </Collapse>
    </div>
  );
};
